<template>
  <div :class="$style.container">
    <div :class="$style.body">
      <div :class="$style.listContainer">
        <div
          v-for="(participant, index) in participantInvitations"
          :key="index"
        >
          <Card :shadowless="true" :class="$style.item">
            <div>
              <p>
                <b class="text-size text-color padding-right20 bold">
                  {{ participant.email }}
                </b>
                <br />
              </p>

              <p :class="$style.textMuted">
                {{ participant.assignedRole }}<br />
                <span v-if="participant.assignedSiteIds">
                  {{ participant.assignedSiteIds.length }}
                  Selected Sites
                </span>
              </p>
            </div>
            <div>
              <a @click="handleEdit(index)" :class="$style.editBtn">
                <b-icon icon="pencil" size="is-small" />
              </a>
              <a @click="handleDelete(index)" :class="$style.deletebtn">
                <b-icon icon="delete" size="is-small" />
              </a>
            </div>
          </Card>
        </div>
      </div>

      <Card
        v-if="participantInvitations && participantInvitations.length === 0"
        :shadowless="true"
        :class="$style.noItem"
      >
        Please invite participants
      </Card>
    </div>

    <div :class="$style.footer">
      <a
        class="button is-link is-text-primary is-borderless is-transparent"
        @click="handleCreateParticipant()"
      >
        + Add Participants
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { Site } from '@/store/modules/site/types/site.types';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import CreateParticipantForm from './CreateParticipantForm.vue';
import Card from '@/components/Card.vue';
import {
  CreateProjectParticipantPayload,
  ParticipantFormData,
  ParticipantSite,
} from '@/store/modules/projects/types/projects.types';
import DeleteModalBox from '../../../../components/DeleteModalBox.vue';
import { cloneData } from '@/utils/general.util';

@Component({
  components: { Card },
})
export default class InviteParticipants extends Vue {
  @Prop()
  public sites!: Site[];

  @Prop({ default: [] })
  public value!: CreateProjectParticipantPayload[];

  // local copy of 'value' for mutation
  public participantInvitations: CreateProjectParticipantPayload[] = [];

  get projectId(): number {
    return +this.$route.params.projectId;
  }

  public getSiteCount(sites: Array<Site | ParticipantSite>): number {
    return sites.filter((siteOrParticipantSite: any) => {
      if (siteOrParticipantSite.site) {
        return siteOrParticipantSite.isDeleted === false;
      }
      return true;
    }).length;
  }

  public transformSitesToParticipantSites(sites: Site[]): ParticipantSite[] {
    return sites.map((associatedSite: Site) => {
      return {
        isDeleted: false,
        site: associatedSite,
      };
    });
  }

  public handleCreateParticipant(): void {
    this.$buefy.modal.open({
      parent: this,
      component: CreateParticipantForm,
      hasModalCard: true,
      props: {
        isEdit: false,
        sites: this.sites,
        participantsList: this.participantInvitations,
      },
      events: {
        submit: (newParticipant: ParticipantFormData) => {
          this.participantInvitations.push(
            (newParticipant as unknown) as CreateProjectParticipantPayload,
          );

          /**
           * The local copy of participants is emitted to parent,
           * and value update will trigger a re-render in watcher.
           *
           * Note:
           * Because projectParticipants is a local copy
           * of 'value' prop, that is passed down via v-model,
           * this input event UPDATES parent value directly.
           * In turn, triggering the local watcher.
           */
          this.$emit('input', this.participantInvitations);
        },
      },
    });
  }

  public handleEdit(participantIndex: number): void {
    this.$buefy.modal.open({
      parent: this,
      component: CreateParticipantForm,
      hasModalCard: true,
      props: {
        isEdit: true,
        sites: this.sites,
        participant: this.participantInvitations[participantIndex],
        participantsList: this.participantInvitations,
      },
      events: {
        submit: (updatedParticipant: ParticipantFormData) => {
          this.participantInvitations[
            participantIndex
          ] = (updatedParticipant as unknown) as CreateProjectParticipantPayload;

          this.$emit('input', this.participantInvitations);
        },
      },
    });
  }

  public handleDelete(participantIndex: number) {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete',
        modalContent: `Are you sure you want to delete this participant?`,
      },
      events: {
        confirm: () => {
          /**
           * During deletion, only remove participant from array
           * when the db record for the participant is not created.
           * (Upsert strategy)
           */
          this.participantInvitations.splice(participantIndex, 1);
          this.$emit('input', this.participantInvitations);
          this.$buefy.toast.open('Participant had been deleted!');
        },
      },
    });
  }

  @Watch('value')
  public replaceValueFromParent(
    participants: CreateProjectParticipantPayload[],
  ) {
    /**
     * The 'value' here is received using v-model.
     * Hence the re-render is triggered when
     * assigning a cloned copy of the updated value.
     */
    this.participantInvitations = cloneData(participants);
  }
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex: 1;
  flex-direction: column;

  .body {
    display: flex;
    flex-direction: column;
    flex: 1;

    .listContainer {
      padding: 0 0.5rem;
      max-height: 500px;
      overflow-y: auto;
    }

    .item {
      min-width: 300px;
      justify-content: space-between;
      background: white;
      margin-bottom: 1rem;

      p {
        line-height: 25px;
      }
    }

    .noItem {
      align-items: center;
      justify-content: center;
      color: $grey;
      background: white;
    }

    .iconButton {
      margin-right: 1rem;
    }
  }

  .footer {
    display: flex;
    flex: 0;
    justify-content: flex-start;
  }
}
.textMuted {
  color: $grey-3;
  line-height: 17px;
  font-size: 14px;
}
.editBtn {
  color: #21b5ea;
  padding-right: 12px;
}
.deletebtn {
  color: #21b5ea;
}
</style>
